import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useDataProvider, type SyncStatusState } from '@/data/provider';
import { readSupabaseEnvironment } from '@/data/supabase/config';
import { useSyncStatusStore } from '@/data/sync/syncStatusStore';
import { useNetworkStatus } from './useNetworkStatus';
import { useDevNetworkOverride } from '@/data/devNetworkOverride';

const PENDING_POLL_INTERVAL = 5000;

export function useSyncStatus() {
  const dataProvider = useDataProvider();
  const { isOnline } = useNetworkStatus();
  const { forceOffline } = useDevNetworkOverride();
  const supabaseEnv = readSupabaseEnvironment();
  const syncConfigured = supabaseEnv.isConfigured;
  const syncEnabled = dataProvider.mode === 'synced' && syncConfigured && supabaseEnv.syncEnabled;
  const { syncing, lastSyncAt, lastError, lastConflict, log } = useSyncStatusStore(
    useShallow((state) => ({
      syncing: state.syncing,
      lastSyncAt: state.lastSyncAt,
      lastError: state.lastError,
      lastConflict: state.lastConflict,
      log: state.log,
    }))
  );

  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let active = true;

    const loadPending = async () => {
      try {
        const count = await dataProvider.sync.getPendingCount();
        if (active) {
          setPendingCount(count);
        }
      } catch {
        if (active) {
          setPendingCount(0);
        }
      }
    };

    loadPending();

    if (typeof window === 'undefined') {
      return () => {
        active = false;
      };
    }

    const interval = window.setInterval(loadPending, PENDING_POLL_INTERVAL);

    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [dataProvider]);

  let status: SyncStatusState = 'online';
  if (dataProvider.mode === 'local') {
    status = 'local';
  } else if (forceOffline) {
    status = 'offline';
  } else if (!syncEnabled) {
    status = 'disabled';
  } else if (!isOnline) {
    status = 'offline';
  } else if (syncing) {
    status = 'syncing';
  } else if (lastError) {
    status = 'error';
  }

  return {
    status,
    syncConfigured,
    syncEnabled,
    pendingCount,
    lastSyncAt,
    lastError,
    lastConflict,
    log,
  };
}
